import React from "react";

import Box from "../Box";
import { Choice, SecRayonType } from "../../types/commercial";

import styled from "styled-components";
import { CText } from "@carrefour/design-system-components-react";
import useBreakPoint from "../../utils/useBreakPoint";
import BaseDashboardCard from "../BaseDashboardCard";
import SelectOption from "../SelectOption";
import { SingleValue } from "react-select";
import { useSearchParams } from "react-router-dom";
import { Store } from "../../types/store";
import { Price } from "../../types/operational";
import { eventTrack } from "../../utils/gtag";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { useRange } from "../../context/rangeContext";
import { Range } from "../../utils/range";

type PriceAnomalyCardProps = {
  title: string;
  listValue: Array<Price>;
  services?: SecRayonType;
  range: string;
  options: Choice[];
  isUnavailable: boolean;
  isLoading: boolean;
  storeType: Store["storeType"];
  openInformationModal: (title?: string) => void;
};

const Text = styled(CText)`
  font-family: system-ui;
`;
const Table = styled.table`
  width: 100%;
  align-items: center;
  border-collapse: collapse;
`;
const Tr = styled.tr``;
const TrCustom = styled.tr<{ isLast: boolean }>`
  border-collapse: collapse;
  border-bottom: ${(props) => (props?.isLast ? "" : "1px solid #f0f3f6")};
`;
const Th = styled.th<{ $textAlign?: string; $width?: string }>`
  text-align: ${(props) => props?.$textAlign};
  width: ${(props) => props?.$width};
`;
const Td = styled.td<{ $textAlign?: string }>`
  text-align: ${(props) => props?.$textAlign};
  padding-block: 8px;
`;
const BoxCustum = styled(Box)`
  width: 60%;
  @media only screen and (max-width: 600px) {
    width: 100%;
  }
`;

const PriceAnomalyCard = ({
  title,
  listValue,
  storeType,
  range,
  options,
  ...rest
}: PriceAnomalyCardProps) => {
  const breakpoint = useBreakPoint();
  const [searchParams, setSearchParams] = useSearchParams();
  const {t} = useTranslation();
  const { setKpiRange, getKpiRange } = useRange();


  const getDefaultValueParams = () => {
    searchParams.set(range, getKpiRange(range) ?? options?.[0]?.value as string);
    setSearchParams(searchParams);
    // first tracking
    eventTrack({
      eventName: "interaction_metric",
      actionName: "filter applied",
      key1: "metric_name",
      value1: title,
      key2: "filter_name",
      value2: options?.[0]?.value,
    });
    return options?.[0]?.value;
  };

  const RangeParam = searchParams.get(range) ?? getDefaultValueParams();

  const handleChange = (choice: SingleValue<Choice>) => {
    const newValue = choice?.value as string;

    if (newValue === RangeParam) {
      return;
    }
    setKpiRange(range, newValue as Range);
    searchParams.set(range, newValue as string);
    setSearchParams(searchParams);
    // tracking changed
    eventTrack({
      eventName: "interaction_metric",
      actionName: "filter applied",
      key1: "metric_name",
      value1: title,
      key2: "filter_name",
      value2: newValue,
    });
  };

  return (
    <BaseDashboardCard
      hasModal={true}
      title={title}
      importedDateTime={listValue?.[0]?.importedDateTime}
      option={
        <BoxCustum py={1}>
          <SelectOption
            list={options}
            value={RangeParam}
            handleChange={handleChange}
          />
        </BoxCustum>
      }
      {...rest}
      size={"big"}
    >
      <Box
        bg={"white"}
        alignItems="center"
        justifyContent={"center"}
        width="100%"
        borderRadius={1}
        pt={1}
      >
        <Table>
          <thead>
            <Tr>
              <Th>
                <Box>
                  <Text></Text>
                </Box>
              </Th>
              <Th
                $textAlign={breakpoint === "phone" ? "left" : "center"}
                $width={"25%"}
              >
                <Box>
                  <Text bold size={"m"} tagStyle={"p"}>
                    {t('operationnel.illisible')}
                  </Text>
                </Box>
              </Th>
              <Th
                $textAlign={breakpoint === "phone" ? "left" : "center"}
                $width={"25%"}
              >
                <Box>
                  <Text bold size={"m"} tagStyle={"p"}>
                    {t('operationnel.inconnu')}
                  </Text>
                </Box>
              </Th>
              <Th
                $textAlign={breakpoint === "phone" ? "center" : "center"}
                $width={"25%"}
              >
                <Box>
                  <Text bold size={"m"} tagStyle={"p"}>
                    {t('operationnel.ecart_prix')}
                  </Text>
                </Box>
              </Th>
            </Tr>
          </thead>
          <tbody>
            {listValue &&
              listValue?.length > 0 &&
              listValue.map((anomaly, index) => {
                return subElements(
                  anomaly as Price,
                  storeType,
                  breakpoint,
                  listValue?.length === index + 1,
                  t
                );
              })}
          </tbody>
        </Table>
      </Box>
    </BaseDashboardCard>
  );
};

export default PriceAnomalyCard;

function subElements(
  anomaly: Price,
  storeType: string,
  breakpoint: string,
  isLast: boolean,
  t: TFunction
) {
  if (storeType === "hypermarket") {
    const item = anomaly as Price;

    return (
      <TrCustom key={"anomaly" + item.sector} isLast={isLast}>
        <Th $textAlign={"left"} $width={"25%"}>
          <Box py={1}>
            <Text bold size={"m"}>
              {item.sector}
            </Text>
          </Box>
        </Th>
        <Th
          $textAlign={breakpoint === "phone" ? "left" : "center"}
          $width={"25%"}
        >
          <Box>
            <Text>
              {item.unreadable
                ? `${new Intl.NumberFormat("fr-FR", {
                    maximumFractionDigits: 0,
                    minimumFractionDigits: 0,
                  }).format(item.unreadable)}`
                : "-"}
            </Text>
          </Box>
        </Th>
        <Th
          $textAlign={breakpoint === "phone" ? "left" : "center"}
          $width={"25%"}
        >
          <Box>
            <Text>
              {item.unknown
                ? `${new Intl.NumberFormat("fr-FR", {
                    maximumFractionDigits: 0,
                    minimumFractionDigits: 0,
                  }).format(item.unknown)}`
                : "-"}
            </Text>
          </Box>
        </Th>
        <Th
          $textAlign={breakpoint === "phone" ? "center" : "center"}
          $width={"25%"}
        >
          <Box>
            <Text>
              {" "}
              {item.priceGap
                ? `${new Intl.NumberFormat("fr-FR", {
                    maximumFractionDigits: 1,
                    minimumFractionDigits: 0,
                  }).format(item.priceGap)}`
                : "-"}
            </Text>
          </Box>
        </Th>
      </TrCustom>
    );
  } else {
    const item = anomaly as Price;
    return (
      <Tr key={"anomaly Total"}>
        <Td $textAlign={"left"} width={"25%"}>
          <Box>
            <Text bold size={"m"}>
              {t('operationnel.total')}
            </Text>
          </Box>
        </Td>
        <Td
          $textAlign={breakpoint === "phone" ? "left" : "center"}
          width={"25%"}
        >
          <Box>
            <Text>
              {item.unreadable
                ? `${new Intl.NumberFormat("fr-FR", {
                    maximumFractionDigits: 0,
                    minimumFractionDigits: 0,
                  }).format(item.unreadable)}`
                : "-"}
            </Text>
          </Box>
        </Td>
        <Td
          $textAlign={breakpoint === "phone" ? "left" : "center"}
          width={"25%"}
        >
          <Box>
            <Text>
              {item.unknown
                ? `${new Intl.NumberFormat("fr-FR", {
                    maximumFractionDigits: 0,
                    minimumFractionDigits: 0,
                  }).format(item.unknown)}`
                : "-"}
            </Text>
          </Box>
        </Td>
        <Td
          $textAlign={breakpoint === "phone" ? "left" : "center"}
          width={"25%"}
        >
          <Box>
            <Text>
              {" "}
              {item.priceGap
                ? `${new Intl.NumberFormat("fr-FR", {
                    maximumFractionDigits: 1,
                    minimumFractionDigits: 0,
                  }).format(item.priceGap)}`
                : "-"}
            </Text>
          </Box>
        </Td>
      </Tr>
    );
  }
}
